import React, { useCallback, useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import {
  CenteredModal,
  Input,
  FormikError,
  SubmitButton,
  useToast,
} from 'components';
import {
  Prospect,
  useCreateProspect,
  ProspectActionEnum,
  useMe,
  useCurrentClient,
} from 'queries';
import { YupValidators } from 'lib/validators';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  description: {
    ...theme.fonts.body,
    marginBottom: theme.marginBase * 3,
  },
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
  },
  sameLine: {
    display: 'flex',
    alignItems: 'center',
    marginRight: -theme.marginBase * 2,
  },
  input: {
    marginRight: theme.marginBase * 2,
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose?: () => any;
  onProspectCreated: (prospect: Prospect) => any;
  title: string;
  description?: string;
  submitText: string;
  action: ProspectActionEnum;
  billingMethod?: string;
  billingCountryCode?: string;
}

const schema = Yup.object().shape({
  firstName: YupValidators.isValidName().required(),
  lastName: YupValidators.isValidName().required(),
  email: Yup.string().email().required(),
  phoneNumber: Yup.string().required(),
  companyName: Yup.string().required(),
  jobTitle: Yup.string().required(),
});

const initialValues: Partial<Prospect> = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  companyName: '',
  jobTitle: '',
};

export const ProspectCreationModal = ({
  isOpen,
  onRequestClose,
  onProspectCreated,
  title,
  description,
  submitText,
  action,
  billingMethod,
  billingCountryCode,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const { mutateAsync: createProspect } = useCreateProspect();
  const { data: me } = useMe(true);
  const { data: client } = useCurrentClient(true);

  const onSubmit = useCallback(
    async (values: Partial<Prospect>) => {
      try {
        const prospect = await createProspect({
          ...values,
          billingMethod,
          billingCountryCode,
          lastAction: action,
          locale: i18n.language,
        });
        await onProspectCreated(prospect);
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [
      createProspect,
      billingMethod,
      billingCountryCode,
      action,
      i18n.language,
      onProspectCreated,
      toast,
    ],
  );

  const canSendDirectly = isOpen && !!me && !!client;
  useEffect(() => {
    if (canSendDirectly && me && client) {
      onSubmit({
        firstName: me.firstName,
        lastName: me.lastName,
        email: me.email,
        phoneNumber: client.phoneNumber,
        companyName: client.organizations[0].name,
        accountId: me.id,
        organizationId: client.organizations[0].id,
      });
    }
  }, [me, client, canSendDirectly, onSubmit]);

  return (
    <CenteredModal
      isOpen={isOpen && !canSendDirectly}
      onRequestClose={onRequestClose}
      title={title}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        <Form>
          {description && <p className={classes.description}>{description}</p>}
          <div className={classes.sameLine}>
            <Input
              name="firstName"
              label={t('billing.prospect.firstName')}
              placeholder="Ex: Albert"
              containerClass={classes.input}
              noMargin
            />
            <Input
              name="lastName"
              label={t('billing.prospect.lastName')}
              placeholder="Ex: Einstein"
              containerClass={classes.input}
              noMargin
            />
          </div>
          <Input
            name="email"
            label={t('billing.prospect.email')}
            placeholder="Ex: albert.einstein@gmail.com"
            type="email"
          />

          <Input
            name="phoneNumber"
            label={t('billing.prospect.phoneNumber')}
            placeholder="Ex: 0987654321"
            type="tel"
          />

          <Input
            name="companyName"
            label={t('billing.prospect.companyName')}
            placeholder="Ex: Apple"
          />
          <Input
            name="jobTitle"
            label={t('billing.prospect.jobTitle')}
            placeholder="Ex: UX Designer"
          />

          <FormikError />

          <div className={classes.buttonsLine}>
            <SubmitButton text={t('billing.prospect.submitText')} />
          </div>
        </Form>
      </Formik>
    </CenteredModal>
  );
};
